import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Repeat, Info, ChevronDown, ChevronUp, Images } from "lucide-react";
import { floorLabel, gbp, condLabel } from "@/lib/api";
import { SavingLine } from "@/components/SavingLine";
import { resolveListingImages } from "@/lib/categoryImages";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel, milesShort } from "@/lib/units";

// A single room's assigned match + its alternatives (public-safe).
export function RoomMatchCard({ room, onReplace, onViewListing }) {
  const { unit } = useUnit();
  const [showAlts, setShowAlts] = useState(false);
  const match = room.match;

  if (!match) {
    return (
      <div className="bg-white border border-dashed border-amber-300 rounded-2xl p-5" data-testid={`room-nomatch-${room.room_id}`}>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-lg text-slate-900">{room.room_name}</h3>
          <span className="text-sm text-slate-500">{dimPairLabel(room.width_m, room.length_m, unit)} · {room.area_m2}m²</span>
        </div>
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3">
          No suitable stock fits this room right now. Try adjusting the flooring type, size or budget — or ask us to keep looking.
        </p>
      </div>
    );
  }

  const l = match.listing;
  const img = resolveListingImages(l);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`room-match-${room.room_id}`}>
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
        <div className="relative w-full sm:w-44 shrink-0">
          <img
            src={img.photos[0]}
            alt={img.alt}
            loading="lazy"
            decoding="async"
            className="w-full h-40 sm:h-full object-cover rounded-xl bg-slate-100"
          />
          {img.isFallback && (
            <span className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white rounded px-1.5 py-0.5 flex items-center gap-1">
              <Info className="w-3 h-3" /> Illustrative image
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">{room.room_name}</p>
              <h3 className="font-display font-semibold text-lg text-slate-900 truncate">{l.title}</h3>
              <p className="text-sm text-slate-500">
                {floorLabel(l.flooring_type)}{l.colour ? ` · ${capitalize(l.colour)}` : ""}{l.condition ? ` · ${condLabel(l.condition)}` : ""}
              </p>
            </div>
            {room.collection_point && (
              <span className="shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#2A5A3B]/10 text-[#2A5A3B]" title="Collection point">
                Point {room.collection_point}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-sm">
            <Meta label="Room" value={dimPairLabel(room.width_m, room.length_m, unit)} />
            <Meta label={l.matching_mode === "dimensions" ? "Remnant" : "Coverage"}
              value={l.matching_mode === "dimensions" ? dimPairLabel(l.width_m, l.length_m, unit) : `${match.packs_required} packs`} />
            <Meta label="Distance" value={match.distance_km != null ? `~${milesShort(match.distance_km)}` : "—"} />
            <Meta label="Spare" value={match.spare_m2 != null ? `${match.spare_m2}m²` : "—"} />
          </div>

          <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-500">
            <MapPin className="w-3.5 h-3.5" /> {l.retailer?.locality || "Greater Manchester area"}
          </div>

          {room.needs_confirmation && (
            <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
              Stairs need professional confirmation — this is an indicative match only.
            </p>
          )}

          <div className="flex items-end justify-between mt-3">
            <div>
              <div className="font-display font-bold text-2xl text-[#0F3D2E]">{gbp(match.room_cost_gbp)}</div>
              <div className="text-[11px] text-slate-400">
                vs {gbp(match.retail_estimate_gbp)} {match.retail_source === "estimated" ? "est. typical retail" : "typical retail"}
              </div>
              <SavingLine
                price={l.price_gbp}
                retail={l.rrp_gbp}
                qty={l.price_basis === "per_pack" && match.packs_required ? match.packs_required : 1}
                testId={`room-saving-${room.room_id}`}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => onViewListing(l)} className="h-9 px-3 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid={`view-listing-${room.room_id}`}>
                View
              </button>
              <button onClick={() => setShowAlts((s) => !s)} className="h-9 px-3 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`alts-toggle-${room.room_id}`}>
                <Repeat className="w-4 h-4" /> Alternatives {showAlts ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">Why: {match.why}</p>
        </div>
      </div>

      {showAlts && (
        <div className="border-t border-slate-100 bg-slate-50 p-4" data-testid={`alts-panel-${room.room_id}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
            <Images className="w-3.5 h-3.5" /> Other options for {room.room_name}
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {room.alternatives.filter((a) => a.listing.id !== l.id).slice(0, 5).map((a) => {
              const ai = resolveListingImages(a.listing);
              return (
                <button
                  key={a.listing.id}
                  onClick={() => { onReplace(room.room_id, a); setShowAlts(false); }}
                  className="flex items-center gap-3 text-left bg-white border border-slate-200 rounded-xl p-2.5 hover:border-[#E05915] transition-colors"
                  data-testid={`alt-${room.room_id}-${a.listing.id}`}
                >
                  <img src={ai.photos[0]} alt={ai.alt} loading="lazy" className="w-14 h-14 rounded-lg object-cover bg-slate-100 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{a.listing.title}</div>
                    <div className="text-xs text-slate-500">{a.listing.colour} · {a.distance_km != null ? `~${milesShort(a.distance_km)}` : ""}</div>
                    <div className="text-sm font-bold text-[#0F3D2E]">{gbp(a.room_cost_gbp)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function Meta({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="font-semibold text-slate-900 text-sm">{value}</div>
    </div>
  );
}
